# Route: /materials/classes/new
# Create a new material class — dedicated full-width create page (lot-receive
# layout idiom). Replaces the former class-list create modal.
# Audience: MES Engineers (md+), blocked on sm

page-class-create:
  type: Page
  archetype: create
  meta:
    title: $t.class_create.meta_title
    auth: required
  # FR-8 (ADR-184) — the shared domain-picker vocabulary. Every domain the
  # caller may pin this create to (their own assignments ∪ descendants);
  # class-create-field-domain hides itself when this resolves to a single
  # domain (nothing to pick) and requires a pick when ambiguous.
  data:
    domainScopes:
      model: DomainScope[]
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - class-create-sm-notice
    - class-create-content

# ── sm viewport notice ───────────────────────────────────────────────────────

class-create-sm-notice:
  type: SmNotice
  content: $t.common.sm_notice_class

# ── Main content (hidden on sm) ──────────────────────────────────────────────

class-create-content:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    responsive:
      sm:
        display: none
  children:
    - class-create-header
    - class-create-form

# ── Header ───────────────────────────────────────────────────────────────────

class-create-header:
  type: PageHeader
  content: $t.class_create.title
  back_route: /materials/classes

# ── Create form ──────────────────────────────────────────────────────────────
# Full-width Form (no max_width cap — one form-width rule everywhere).
# Input meta applies touch-safe min_height, a focus outline, required indicator +
# validation timing; authors do not spell these out per field. The focus ring on a
# form control comes from InputCssGenerator.cs:96-99 (`input:focus, textarea:focus,
# select:focus`), which keys on :focus rather than :focus-visible, so it also shows
# on mouse click. The :host(:focus-visible) rule FocusCssGenerator writes does NOT
# render — see design-system/accessibility.md § Focus visible ring.

class-create-form:
  type: Form
  model: ItemClass
  a11y:
    label: $t.class_create.form_label
  action:
    type: create
    model: ItemClass
    on_success: navigate(/materials/classes)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - class-create-field-name
    - class-create-field-nature
    - class-create-field-tracking
    - class-create-field-domain
    - class-create-field-description
    - class-create-form-actions

class-create-field-name:
  type: Input
  input_type: text
  bind:
    value: name
  label: $t.class_list.col_name
  required: true
  a11y:
    label: $t.class_list.col_name

# F75 #133 — Material Nature is a controlled vocabulary: the select's options
# + localized labels resolve from the ItemClass model field (models.cir.yaml
# `itemNature` values/value_labels → enums.material_nature locale keys),
# the same model-driven shape the tracking-mode select below uses.
class-create-field-nature:
  type: Input
  input_type: select
  bind:
    value: itemNature
  label: $t.class_list.col_nature
  a11y:
    label: $t.class_list.col_nature

class-create-field-tracking:
  type: Input
  input_type: select
  bind:
    value: defaultTrackingMode
  label: $t.class_list.col_tracking
  a11y:
    label: $t.class_list.col_tracking

# FR-8 (ADR-184) — shared domain-picker fragment. Controlled-vocabulary select
# (design-system/patterns.md § Controlled-vocabulary field) over the caller's
# own domain scope (page data.domainScopes, GET /api/users/me/domain-scopes).
# Submits the domain node id as `domainId` on the create request (the API DTO
# CreateItemClassRequest.DomainId is always optional — the resolver falls
# back to the caller's single domain when the field is absent from the POST).
#
# visible_when hides the field entirely when the caller's scope is
# unambiguous (domainScopes resolves to at most one domain — nothing to pick);
# while it is hidden the field is neither validated nor submitted, and the
# resolver falls back to the caller's single domain. When the scope is
# ambiguous the field shows and required: true makes the pick mandatory
# (design-system/components.md § Conditional fields — "Hidden fields are not
# in the tab order and are not submitted").
#
# This is the ONE screen among the four reference-data create screens (reason
# vocabulary, tracking form, item class, asset class) where the picker is
# fully wired end-to-end: it is the only one of the four whose create form is
# a native CIR Form posting straight to a domainId-aware DTO endpoint
# (POST /api/items/classes). The other three route through hand-authored
# widgets (xp87-vocab-form.js posts to a domainId-aware endpoint but owns its
# own payload builder; xp87-yaml-config-editor.js posts a raw `{ yaml }` body
# with no domainId concept at any layer) — adopting the picker there needs a
# change to those widgets.
# Any future adopter reuses this exact data-source + field shape verbatim (no
# CIR include/fragment mechanism exists in this codebase to share the block
# structurally — every real controlled-vocabulary picker in ui/src/pages is a
# hand-replicated, consistently-shaped block; see lot-receive.cir.yaml /
# def-create.cir.yaml for the UnitOfMeasure/LocationOption precedent this
# mirrors).
class-create-field-domain:
  type: Input
  input_type: select
  label: $t.domain_picker.label
  placeholder: $t.domain_picker.placeholder
  bind:
    value: domainId
    options: domainScopes
  visible_when: "domainScopes.length > 1"
  required: true
  helper: $t.domain_picker.required_hint
  a11y:
    label: $t.domain_picker.label

class-create-field-description:
  type: Input
  input_type: textarea
  bind:
    value: description
  label: $t.class_create.field_description
  a11y:
    label: $t.class_create.field_description

# FormActions meta: Cancel (outlined secondary) then Create (filled primary)
# in one right-aligned row; sticky-bottom on sm/md, inline on lg+.

class-create-form-actions:
  type: FormActions
  submit_label: $t.class_list.btn_create
  cancel_route: /materials/classes
